












import { Component, Prop, Vue, Provide } from 'vue-property-decorator'

import SAPSailingAnalyticsAPI, { Regatta, BoatClass } from '@/assets/scripts/sapsa-api'

import RegattaListContent from '@/components/regattaListContent.vue'

@Component({
    components: {
        RegattaListContent
    }
})
export default class RegattaList extends Vue {
    private analytics!: SAPSailingAnalyticsAPI

    @Prop()
    private server?: string

    @Prop()
    private apiToken?: string

    @Prop()
    private eventId?: string

    @Prop()
    private regattaName?: string

    @Prop()
    private title?: string|false

    @Prop({ default: false })
    private noStyles?: boolean

    @Prop({ default: 'tab' })
    @Provide()
    private linkMode?: 'tab'|'popup'

    @Provide()
    private regattas: Regatta[] = []

    @Provide()
    private boatClasses: BoatClass[] = []

    private isLoading = true

    private refreshInterval = 60 * 1000 // 60s

    private mounted () {
        this.initSapsa()
    }

    private rot13 (str: string): string {
        const input = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
        const output = 'NOPQRSTUVWXYZABCDEFGHIJKLMnopqrstuvwxyzabcdefghijklm'
        const index = (x: string) => input.indexOf(x)
        const translate = (x: string) => index(x) > -1 ? output[index(x)] : x
        return str.split('').map(translate).join('')
    }

    private transformAPIToken (input: string): string {
        return this.rot13(atob(input.split('').reverse().join('')))
    }

    // SAP Sailing Analytics
    private async initSapsa () {
        if (this.server == null || this.apiToken == null) {
            console.warn('Please supply a server and api token')
            return
        }

        let apiToken = this.apiToken
        if (apiToken.length > 50) {
            apiToken = this.transformAPIToken(this.apiToken)
        }

        this.analytics = new SAPSailingAnalyticsAPI(this.server, apiToken)

        // Get list of boat classes on the server
        // This only needs to be done once, since classes are unlikely to change
        try {
            const boatClasses = await this.analytics.getBoatClasses()
            boatClasses.forEach((boatClass) => {
                if (boatClass.iconUrl != null) {
                    boatClass.setIconUrl(this.server + boatClass.iconUrl)
                }
                this.boatClasses.push(boatClass)
            })
        } catch {
            console.warn('Error fetching boat classes from server')
        }

        // use the newest event on server if id has not been provided
        // copy into new var since we dont want to mutate the user passed prop
        let eventId = this.eventId
        if (eventId == null) {
            try {
                const events = await this.analytics.getEvents()
                const event = events.sort((a, b) => a.startDate != null && b.startDate != null && a.startDate > b.startDate ? -1 : 1).shift()
                if (event == null) {
                    console.warn('No events on server')
                    return
                }
                eventId = event.id
            } catch {
                console.warn('Error fetching events from server')
            }
        }

        if (eventId == null) {
            console.warn('No event id was provided and no event found on server')
            return
        }

        // Initial load of regattas for the event
        // If there is an error we substitute an empty map,
        // effectivly skipping all additional calls later
        await this.analytics.getRegattasForEvent(eventId).catch(() => {
            console.warn('Error fetching regattas from server')
            return new Map<string, Regatta>()
        }).then((regattas) => {
            regattas.forEach((regatta) => {
                // Filter regattas if regatta name was passed
                // (note that the initial leaderboardgroup request will still happen as part of getRegattasForEvent())
                if (this.regattaName != null && regatta.name !== this.regattaName) {
                    return
                }

                // Remove event and leaderboard group id from url to enable viewing raceboards without event:read rights
                regatta.races.forEach((race) => {
                    if (race.raceViewerUrls[0] != null) {
                        const url = new URL(race.raceViewerUrls[0])
                        url.searchParams.delete('eventId')
                        url.searchParams.delete('leaderboardGroupId')
                        race.raceViewerUrls[0] = url.toString()
                    }
                })
                this.regattas.push(regatta)
            })
            if (this.regattas.length !== 0) {
                this.isLoading = false
            }
        })

        // Initial leaderboard requests
        // Needed to fill out boatClass for the regatta
        // and get start times for all races (to be later mutated by the /times calls)
        const leaderboardRequests = this.regattas.map((regatta) => {
            return this.analytics.loadLeaderboardForRegatta(regatta).then((regatta) => {
                const regattaIndex = this.regattas.findIndex((savedRegatta) => savedRegatta.name === regatta.name)
                if (regattaIndex !== -1) {
                    this.regattas[regattaIndex] = regatta
                }
            })
        })

        await Promise.all(leaderboardRequests).catch(() => {
            console.warn('Error fetching additional regatta leaderboard from server')
        })

        // We load the leaderboard once for every regatta at the beginning (see above). This gives us the start times for all races as of right now.
        // Then we query times for the last race that has a start time (or the first one, if none have a start time) and check if tracking has stopped.
        // - If yes, then the race has finished,
        // - If no, then then the race is still running.
        // In both cases all races before that are probably finished.
        // Repeat.

        // Kickstart refresher for status data (times request)
        this.fetchTimes()

        // Theoretically more races could show up on the server which would mean
        // that we have to requery getRegattasForEvent() or loadRegattaDetails every second.
        // However, we can assume that the race structure is set in stone for the Olympics, therefore saving us some requests.
    }

    // Refill regattas with status info from the times calls every x seconds
    private fetchTimes (): void {
        const now = new Date()

        const timesRequests = this.regattas.map((regatta) => {
            const firstRaceIndexWithNoStartTime = regatta.races.findIndex((race) => race.startTime == null)
            // Fetch times for the race before the one with no start time
            let fetchTimesForIndex = firstRaceIndexWithNoStartTime - 1
            // If all races have a start time, fetch times for the last race
            if (firstRaceIndexWithNoStartTime === -1) {
                fetchTimesForIndex = regatta.races.length - 1
            // If the first race has no start time, fetch times for the first race
            } else if (firstRaceIndexWithNoStartTime === 0) {
                fetchTimesForIndex = 0
            }

            // If the race we are fetching times for is finished as per the last call,
            // fetch the next one (or this one again if it is the last one)
            const endOfTracking = regatta.races[fetchTimesForIndex].endOfTracking
            if (endOfTracking != null && endOfTracking < now) {
                fetchTimesForIndex = Math.min(regatta.races.length - 1, fetchTimesForIndex + 1)
            }

            return this.analytics.loadTimesForRegattaAndRace(regatta, regatta.races[fetchTimesForIndex])
        })

        Promise.all(timesRequests).catch(() => {
            console.warn('Error fetching additional race times from server')
        })

        // Requery in x seconds independent of whether the previous request was successfull or not
        setTimeout(this.fetchTimes, this.refreshInterval)
    }
}
